import { setClassNames } from '../utilities/classes';
import { setImageUrl } from '../utilities/images';

/**
 * Sets the background classes based on configuration parameters
 * Returns string with joined classes or null if no classes are defined
 * @param {string} imageLocation - A string value for image location (i.e. 'foreground' or 'background')
 * @param {boolean} foregroundImageCircle - A boolean value for displaying foreground image as a circle
 * @param {string} foregroundImageMargin - A string value for image margin class
 * @param {string} foregroundImagePadding - A string value for image padding class
 * @param {string} foregroundImageAlignment - A string value for image alignment class
 * @param {string} backgroundColor - A string value for background color class
 */
export const setBackgroundClassNames = (
	imageLocation,
	foregroundImageCircle,
	foregroundImageMargin,
	foregroundImagePadding,
	foregroundImageAlignment,
	backgroundColor,
	...classes
) => {
	const classNames = [...classes];

	if (imageLocation === 'foreground') {
		classNames.push('cta-foreground');
		classNames.push(foregroundImageMargin);
		classNames.push(foregroundImagePadding);
		classNames.push(foregroundImageAlignment);

		if (foregroundImageCircle) {
			classNames.push('img-circle');
		}
	} else {
		classNames.push(backgroundColor);
		classNames.push('cta-background', 'h-100', 'w-100');

		if (imageLocation === 'background') {
			classNames.push('position-absolute');
		}
	}

	return setClassNames.apply(this, classNames);
};

/**
 * Sets the background styles based on configuration parameters
 * Returns an object with all styles defined
 * @param {boolean} showImage - A boolean value for displaying image
 * @param {string} imageLocation - A string value for image location (i.e. 'foreground' or 'background')
 * @param {string} imageUrl - A string value for image URL
 * @param {string} imagePosition - A string value for background position
 * @param {string} imageSize - A string value for background size
 * @param {string} backgroundGradient - A string value for background gradient
 * @param {string} backgroundRepeat - A string value for background repeat
 * @param {number} foregroundImageHeight - A number value for height
 * @param {number} foregroundImageWidth - A number value for width
 * @param {boolean} hasResponsiveImage - A boolean value for whether image is responsive
 * @param {string} backgroundImage - A string value for background image src
 */
export const setBackgroundStyles = (
	showImage,
	imageLocation,
	imageUrl,
	imagePosition,
	imageSize,
	backgroundGradient,
	backgroundRepeat,
	foregroundImageHeight,
	foregroundImageWidth,
	hasResponsiveImage,
	backgroundImage
) => {
	const styles = {};
	let backgroundImageUrl = backgroundImage;

	if (showImage) {
		if (!hasResponsiveImage) {
			backgroundImageUrl = setImageUrl(
				imageUrl,
				foregroundImageHeight,
				foregroundImageWidth,
				imageLocation
			);
		}

		if (imagePosition) {
			styles.backgroundPosition = imagePosition;
		}
		if (imageSize) {
			styles.backgroundSize = imageSize;
		}

		if (imageLocation === 'foreground') {
			styles.flex = 'none';

			if (foregroundImageHeight) {
				styles.height = `${foregroundImageHeight}px`;
			}
			if (foregroundImageWidth) {
				styles.width = `${foregroundImageWidth}px`;
			}
			if (imageUrl) {
				styles.backgroundImage = `url('${backgroundImageUrl}')`;
				styles.backgroundRepeat = 'no-repeat';
			}
		} else {
			if (backgroundGradient && imageUrl) {
				styles.backgroundImage = `${backgroundGradient}, url('${backgroundImageUrl}')`;
			} else if (backgroundGradient) {
				styles.backgroundImage = backgroundGradient;
			} else if (imageUrl) {
				styles.backgroundImage = `url('${backgroundImageUrl}')`;
			}

			if (backgroundRepeat) {
				styles.backgroundRepeat = backgroundRepeat;
			}
		}
	} else if (backgroundGradient) {
		styles.backgroundImage = backgroundGradient;
	}

	return styles;
};



// WEBPACK FOOTER //
// ./src/utilities/background.js